import React, { useState, useCallback, useRef, useEffect } from "react"
import styled from "styled-components"
import { useLocation } from "react-router-dom"
import FilterSection, {
  FilterValues,
} from "../components/filters/FilterSection"
import InfluencerList from "../components/influencers/InfluencerList"
import { Influencer } from "../types/influencer"
import { DataService } from "../services"

const PageWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  padding: ${({ theme }) => theme.spacing[4]};
`

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const FilterContainer = styled.div`
  margin: 0 auto;
  max-width: 700px;
  width: 100%;
`

const InfluencerResearchToolPage: React.FC = () => {
  const location = useLocation()
  const ignoreFetchResultsRef = useRef(false)
  const pageWrapperRef = useRef<HTMLDivElement>(null)

  const [selectedFilters, setSelectedFilters] = useState<FilterValues>({
    industry: "",
    country: "",
    budget: "",
    businessEmail: "",
    companyName: "",
    phoneNumber: "",
  })

  const [influencers, setInfluencers] = useState<Influencer[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState<boolean>(false)
  const [showResults, setShowResults] = useState<boolean>(false)

  useEffect(() => {
    // Helper to send height to parent
    const sendHeight = () => {
      if (pageWrapperRef.current) {
        const height = pageWrapperRef.current.scrollHeight

        window.parent.postMessage({ type: "resize", height }, "*")
      }
    }

    // Observe PageWrapper changes
    const observer = new ResizeObserver(() => {
      sendHeight()
    })

    if (pageWrapperRef.current) {
      observer.observe(pageWrapperRef.current)
    }

    // Also send height when page first loads or layout stabilizes
    window.addEventListener("load", () => setTimeout(sendHeight, 1000))

    // Initial send after mount
    sendHeight()

    // Cleanup on unmount
    return () => {
      observer.disconnect()
      window.removeEventListener("load", sendHeight)
    }
  }, [])

  // Helper to get budget range for service
  const getBudgetRangeForService = (
    budgetKey: string
  ): { minBudget?: number; maxBudget?: number } => {
    const budgetMap: Record<string, { min: number; max: number }> = {
      "0-3000": { min: 0, max: 2999 },
      "0-4999": { min: 0, max: 4999 },
      "3000-5000": { min: 3000, max: 4999 },
      "5000-10000": { min: 5000, max: 9999 },
      "10000+": { min: 10000, max: Number.MAX_SAFE_INTEGER },
      "5000-15000": { min: 5000, max: 14999 },
      "15000-25000": { min: 15000, max: 24999 },
      "25000-50000": { min: 25000, max: 49999 },
      "50000+": { min: 50000, max: Number.MAX_SAFE_INTEGER },
    }
    const range = budgetMap[budgetKey]
    if (range) {
      return { minBudget: range.min, maxBudget: range.max }
    }
    return {}
  }

  const fetchInfluencers = useCallback(async (filterValues: FilterValues) => {
    setIsLoading(true)

    const budgetRangeParams = filterValues.budget
      ? getBudgetRangeForService(filterValues.budget)
      : {}

    const paramsForService = {
      industry: filterValues.industry,
      country: filterValues.country,
      businessEmail: filterValues.businessEmail,
      ...(budgetRangeParams.minBudget !== undefined && {
        minBudget: budgetRangeParams.minBudget,
      }),
      ...(budgetRangeParams.maxBudget !== undefined && {
        maxBudget: budgetRangeParams.maxBudget,
      }),
    }

    try {
      const response = await DataService.getInfluencers(paramsForService as any)

      if (ignoreFetchResultsRef.current) {
        setIsLoading(false)
        return
      }

      setInfluencers(response.influencers)
      setHasSubmittedEmail(true)
      setShowResults(true)

      // Save user info and email submission status
      try {
        await DataService.saveUserInfo(filterValues.businessEmail, filterValues)
        localStorage.setItem("emailSubmitted", "true")
      } catch (error) {
        // Silent fail
      }

      // Save selected filters to localStorage
      localStorage.setItem("selectedFilters", JSON.stringify(filterValues))
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleFilterSubmit = async (filterValues: FilterValues) => {
    setSelectedFilters(filterValues)
    setIsLoading(true)

    // Save user info and email submission status for tracking
    try {
      await DataService.saveUserInfo(filterValues.businessEmail, filterValues)
      localStorage.setItem("emailSubmitted", "true")
      localStorage.setItem("selectedFilters", JSON.stringify(filterValues))
    } catch (error) {
      // Silent fail
    } finally {
      setIsLoading(false)
    }

    // Build URL with filter parameters for the new tab
    const params = new URLSearchParams()

    if (filterValues.industry) params.set("industry", filterValues.industry)
    if (filterValues.country) params.set("country", filterValues.country)
    if (filterValues.budget) params.set("budget", filterValues.budget)
    if (filterValues.businessEmail)
      params.set("businessEmail", filterValues.businessEmail)
    if (filterValues.companyName)
      params.set("companyName", filterValues.companyName)
    if (filterValues.phoneNumber)
      params.set("phoneNumber", filterValues.phoneNumber)

    // Add current URL parameters (like UTM params)
    const currentParams = new URLSearchParams(location.search)
    currentParams.forEach((value, key) => {
      if (!params.has(key)) {
        params.set(key, value)
      }
    })

    // Build the results URL and send to parent to open in new tab
    const resultsUrl = `${
      window.location.origin
    }/influencer-results?${params.toString()}`

    // Send message to parent window to open the URL (for iframe usage)
    window.parent.postMessage(
      { type: "openUrl", url: resultsUrl, target: "_blank" },
      "*"
    )

    window.open(resultsUrl, "_blank")
  }

  const handleChangeFilters = () => {
    ignoreFetchResultsRef.current = true
    setShowResults(false)
    localStorage.removeItem("selectedFilters")
  }

  const handleChange = (field: keyof FilterValues, value: string) => {
    const updatedFilters = {
      ...selectedFilters,
      [field]: value,
    }
    setSelectedFilters(updatedFilters)

    // Save email if valid
    if (
      field === "businessEmail" &&
      value &&
      value.includes("@") &&
      value.includes(".")
    ) {
      try {
        DataService.saveUserInfo(value, updatedFilters).catch(() => {})
      } catch (error) {
        // Silent fail
      }
    }
  }

  // Check for query parameters on mount and auto-load results
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    // Check if we have filter parameters in the URL
    const hasFilterParams =
      searchParams.has("industry") ||
      searchParams.has("country") ||
      searchParams.has("budget") ||
      searchParams.has("businessEmail")

    if (hasFilterParams) {
      const filterValues: FilterValues = {
        industry: searchParams.get("industry") || "",
        country: searchParams.get("country") || "",
        budget: searchParams.get("budget") || "",
        businessEmail: searchParams.get("businessEmail") || "",
        companyName: searchParams.get("companyName") || "",
        phoneNumber: searchParams.get("phoneNumber") || "",
      }

      setSelectedFilters(filterValues)
      fetchInfluencers(filterValues)
    }
  }, [location.search, fetchInfluencers])

  const generateSignupUrl = useCallback(
    (filterValues: FilterValues): string => {
      const params = new URLSearchParams()

      if (filterValues.industry) {
        if (filterValues.industry === "other") {
          params.set("source_industry", "not_listed")
        } else {
          params.set("source_industry", filterValues.industry)
        }
      }

      if (filterValues.country) {
        if (filterValues.country === "other") {
          params.set("source_country", "not_listed")
        } else {
          params.set("source_country", filterValues.country)
        }
      }

      if (filterValues.budget) {
        params.set("source_budget", filterValues.budget)
      }

      if (filterValues.businessEmail) {
        params.set("email", filterValues.businessEmail)
      }

      // Add current URL parameters (like UTM params)
      const currentParams = new URLSearchParams(location.search)
      currentParams.forEach((value, key) => {
        if (!params.has(key)) {
          params.set(key, value)
        }
      })

      return `https://business.hypefy.ai/auth/signup${
        params.toString() ? "?" + params.toString() : ""
      }`
    },
    [location.search]
  )

  const getSignupUrl = () => {
    return generateSignupUrl(selectedFilters)
  }

  return (
    <PageWrapper ref={pageWrapperRef}>
      <ContentContainer>
        {!showResults ? (
          <FilterContainer>
            <FilterSection
              initialValues={selectedFilters}
              onFieldChange={handleChange}
              onSubmit={handleFilterSubmit}
              isLoading={isLoading}
              signupUrl={getSignupUrl()}
            />
          </FilterContainer>
        ) : (
          <InfluencerList
            influencers={influencers}
            isLoading={isLoading}
            hasSubmittedEmail={hasSubmittedEmail}
            currentFilters={selectedFilters}
            onChangeFilters={handleChangeFilters}
            signupUrl={getSignupUrl()}
            showCTA={false}
          />
        )}
      </ContentContainer>
    </PageWrapper>
  )
}

export default InfluencerResearchToolPage
